// Secure admin authentication utilities

import { generateSecureToken } from './security';

// Store admin session token (in production, use secure HTTP-only cookies)
const ADMIN_SESSION_KEY = 'admin_session_token';

/**
 * Verify admin password via server-side API
 */
export const verifyAdminPassword = async (password: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/admin-auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    const data = await response.json();

    if (data.success) {
      // Store session data
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(data.session));
      return true;
    }

    return false;
  } catch (error) {
    console.error('Admin authentication error:', error);
    return false;
  }
};

/**
 * Create admin session
 */
export const createAdminSession = (): string => {
  const sessionToken = generateSecureToken(64);
  const sessionData = {
    token: sessionToken,
    createdAt: Date.now(),
    expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
  };
  
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(sessionData));
  return sessionToken;
};

/**
 * Verify admin session
 */
export const verifyAdminSession = (): boolean => {
  try {
    const sessionData = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!sessionData) return false;

    const session = JSON.parse(sessionData);
    const now = Date.now();

    // Check if session is expired
    if (now > session.expiresAt) {
      localStorage.removeItem(ADMIN_SESSION_KEY);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Session verification error:', error);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    return false;
  }
};

/**
 * Clear admin session
 */
export const clearAdminSession = (): void => {
  localStorage.removeItem(ADMIN_SESSION_KEY);
};

/**
 * Get session token
 */
export const getSessionToken = (): string | null => {
  try {
    const sessionData = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!sessionData) return null;

    const session = JSON.parse(sessionData);
    return session.token;
  } catch (error) {
    return null;
  }
};

/**
 * Rate limiting for login attempts
 */
class LoginRateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();
  private maxAttempts = 5;
  private windowMs = 15 * 60 * 1000; // 15 minutes

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const attempt = this.attempts.get(identifier);

    if (!attempt || now > attempt.resetTime) {
      this.attempts.set(identifier, { count: 1, resetTime: now + this.windowMs });
      return true;
    }

    if (attempt.count >= this.maxAttempts) {
      return false;
    }

    attempt.count++;
    return true;
  }

  reset(identifier: string): void {
    this.attempts.delete(identifier);
  }
}

export const loginRateLimiter = new LoginRateLimiter(); 